import {
  Button,
  CheckboxField,
  Flex,
  Modal,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import { pluralize } from '@iheartjane/utils';
import type { RowSelectionState } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

import { useUpdateBulkProductsAsync } from '@jane/business-admin/data-access';
import { useStoreId } from '@jane/business-admin/hooks';

interface RevertModalProps {
  onBulkEditModalClose: () => void;
  onClose: () => void;
  onSelectedProductsReset: (value: RowSelectionState) => void;
  open: boolean;
  selectedProducts: RowSelectionState;
}

export const RevertModal = ({
  open,
  onClose,
  selectedProducts,
  onBulkEditModalClose,
  onSelectedProductsReset,
}: RevertModalProps) => {
  const toast = useToast();
  const storeId = useStoreId();
  const {
    mutateAsync: bulkUpdateProductsAsync,
    isPending: bulkUpdateProductsLoading,
    isSuccess: bulkUpdateProductsSuccess,
    isError: bulkUpdateProductsError,
  } = useUpdateBulkProductsAsync(storeId);

  const [revertOptions, setRevertOptions] = useState({
    images: false,
    descriptions: false,
  });

  const selectedProductIds = Object.keys(selectedProducts);
  const selectedCount = selectedProductIds.length;

  const handleCheckboxChange =
    (field: 'images' | 'descriptions') => (checked: boolean) => {
      setRevertOptions((prev) => ({
        ...prev,
        [field]: checked,
      }));
    };

  useEffect(() => {
    if (bulkUpdateProductsSuccess) {
      onClose();
      onBulkEditModalClose();
      onSelectedProductsReset({});
      toast.add({
        label:
          'Update submitted! Depending on your product count, it may take up to a few minutes. You can safely navigate away.',
        variant: 'success',
      });
    }
  }, [bulkUpdateProductsSuccess]);

  useEffect(() => {
    if (bulkUpdateProductsError) {
      toast.add({
        label: 'Bulk update failed!',
        variant: 'error',
      });
    }
  }, [bulkUpdateProductsError]);

  const onSubmit = () => {
    const revertData: Record<string, any> = {};

    if (revertOptions.descriptions) {
      revertData['description'] = '';
    }

    if (Object.keys(revertData).length > 0 || revertOptions.images) {
      bulkUpdateProductsAsync({
        data: revertData,
        productIds: selectedProductIds,
        deletePhotos: !!revertOptions.images,
      });
    }
  };

  const handleDismiss = () => {
    onClose();
  };

  const disableConfirm = Object.values(revertOptions).every(
    (checked) => checked === false
  );

  return (
    <Modal
      onRequestClose={onClose}
      open={open}
      variant="dialogue"
      appId={'parent'}
    >
      <Modal.Content>
        <Flex
          width="100%"
          minWidth={512}
          flexDirection="column"
          alignItems="center"
        >
          <Typography variant="title-bold" mb={16}>
            Revert products
          </Typography>
          <Typography textAlign="center" mb={24}>
            You are attempting to revert{' '}
            {pluralize({ number: selectedCount, noun: 'product' })}.
          </Typography>

          <Flex
            width="100%"
            flexDirection="row"
            justifyContent="center"
            mb={32}
          >
            <Flex mr={40}>
              <CheckboxField
                label="Images"
                name="images"
                checked={revertOptions.images}
                onChange={handleCheckboxChange('images')}
              />
            </Flex>
            <Flex>
              <CheckboxField
                label="Descriptions"
                name="descriptions"
                checked={revertOptions.descriptions}
                onChange={handleCheckboxChange('descriptions')}
              />
            </Flex>
          </Flex>

          <Flex width="100%">
            <Button
              mr={16}
              full
              variant="tertiary"
              label="Dismiss"
              onClick={handleDismiss}
            />
            <Button
              mr={16}
              full
              variant="primary"
              label="Confirm"
              data-testid="confirm-revert-button"
              onClick={onSubmit}
              disabled={disableConfirm || bulkUpdateProductsLoading}
              loading={bulkUpdateProductsLoading}
            />
          </Flex>
        </Flex>
      </Modal.Content>
    </Modal>
  );
};
